export const fragmentShader = `

varying vec3 vWorldNormal;
varying vec3 vWorldPosition;
varying vec3 vLookDirection;
varying vec2 vUv;

uniform sampler2D noiseTexture;
uniform sampler2D backfaceNormalTexture;
uniform sampler2D liquidNormalTexture;
uniform sampler2D innerContentTexture;
uniform sampler2D innerContentTextureMask;
uniform samplerCube envMap;

uniform vec2 resolution;
uniform float showNormal;
uniform float opacity;
uniform float time;
uniform float rippleAmount;

float fresnel(vec3 eyeVector, vec3 worldNormal) {

	return pow( 1.0 + dot( eyeVector, worldNormal ), 3.0 );

}

float borders(vec2 uv, float strokeWidth) {

  vec2 borderBottomLeft = smoothstep(vec2(0.0), vec2(strokeWidth), uv);
  vec2 borderTopRight = smoothstep(vec2(0.0), vec2(strokeWidth), 1.0 - uv);
  return 1.0 - borderBottomLeft.x * borderBottomLeft.y * borderTopRight.x * borderTopRight.y;

}

// http://the-witness.net/news/2012/02/seamless-cube-map-filtering/
vec3 fix_cube_lookup( vec3 v, float cube_size, float lod ) {

	float M = max(max(abs(v.x), abs(v.y)), abs(v.z));

	float scale = 1.0 - exp2(lod) / cube_size;

	if (abs(v.x) != M) v.x *= scale;
	if (abs(v.y) != M) v.y *= scale;
	if (abs(v.z) != M) v.z *= scale;

	return v;

}

vec3 rgb2hsb( in vec3 c ){
    vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
    vec4 p = mix(vec4(c.bg, K.wz),
                 vec4(c.gb, K.xy),
                 step(c.b, c.g));
    vec4 q = mix(vec4(p.xyw, c.r),
                 vec4(c.r, p.yzx),
                 step(p.x, c.r));
    float d = q.x - min(q.w, q.y);
    float e = 1.0e-10;
    return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)),
                d / (q.x + e),
                q.x);
}

//  Function from Iñigo Quiles
//  https://www.shadertoy.com/view/MsS3Wc
vec3 hsb2rgb( in vec3 c ){
    vec3 rgb = clamp(abs(mod(c.x*6.0+vec3(0.0,4.0,2.0),
                             6.0)-3.0)-1.0,
                     0.0,
                     1.0 );
    rgb = rgb*rgb*(3.0-2.0*rgb);
    return c.z * mix(vec3(1.0), rgb, c.y);
}


void main() {

  float roughness = 0.0;
  float maxRougnessAmount = 4.0;

  vec2 uv = gl_FragCoord.xy / resolution;

  vec3 lookDirection = normalize( vLookDirection );

  float ior = 1.2;

  vec3 noise = texture2D( noiseTexture, vUv * 0.05 ).rgb;

  vec3 bumpNormalA = texture2D( liquidNormalTexture, ( vUv * 0.4  ) + ( time * 0.2 )).rgb;
  vec3 bumpNormalB = texture2D( liquidNormalTexture, ( vUv * 0.5 ) - ( time * 0.3 ) ).rgb;

  vec3 innerRefractionNormalA = texture2D( liquidNormalTexture, ( vUv * 1.0 ) - ( time * 0.5 ) ).rgb * 2.0 - 1.0;
  vec3 innerRefractionNormalB = texture2D( liquidNormalTexture, ( vUv * 1.8 ) + ( time * 1.0 ) ).rgb * 2.0 - 1.0;

  vec3 innerRefractionNormal = mix( innerRefractionNormalA, innerRefractionNormalB, 0.5 ) * rippleAmount;

  vec3 refractedInner = refract( lookDirection, normalize( vWorldNormal ), 1.0 / 1.8 );

  vec3 backFaceNormals = texture2D( backfaceNormalTexture, uv + ( refractedInner.xy * 0.02 ) + ( innerRefractionNormal.rg * ( 0.02 * rippleAmount ) ) ).rgb;

  float a = 0.83 * ( 1.0 - roughness );

  vec3 normal = vWorldNormal * ( 1.0 - a ) - backFaceNormals * a;

  vec3 refractedRay = refract( lookDirection, normal, 1.0 / ior );

  vec3 reflectedRay = reflect( lookDirection, normal + (innerRefractionNormal * 0.05) );

  vec3 reflectionNormal = vWorldNormal + ( innerRefractionNormal * 0.05 );
  vec3 reflectedRayOuter = reflect( lookDirection, normalize( reflectionNormal + ( innerRefractionNormal * 0.05 ) ) );

  refractedRay				= fix_cube_lookup( refractedRay, 256.0, maxRougnessAmount * roughness );
  reflectedRay				= fix_cube_lookup( reflectedRay, 256.0, maxRougnessAmount * roughness );
  reflectedRayOuter   = fix_cube_lookup( reflectedRayOuter, 256.0, maxRougnessAmount * roughness );

  vec3 envRefractionOut =  textureCube( envMap, refractedRay, maxRougnessAmount * roughness ).rgb;
  vec3 envReflectionOut =  textureCube( envMap, reflectedRay, maxRougnessAmount * roughness ).rgb;
  vec3 envReflectionOuterOut = textureCube( envMap, reflectedRayOuter, maxRougnessAmount * roughness ).rgb;

  float specStrength = 0.8 * ( 1.0 - roughness );

  vec3 lightPosition  = vec3( 10.0 * sin( time ), 10.0, 10.0 * sin( time ) );
  vec3 lightDirection = normalize( vWorldPosition - lightPosition );

  vec3 reflectDir = reflect( -lightDirection, vWorldNormal );

  float spec = pow( max( dot( lookDirection, reflectDir ), 0.0 ), 32. );
  vec3 specular = specStrength * spec * vec3( 1.0, 1.0, 1.0 );

  float f = fresnel( lookDirection, normalize( vWorldNormal ) );

  vec3 outColor = mix( vec3( 1.0 ), ( envReflectionOut * 0.8 ) + ( envRefractionOut * 0.8 ), 1.0 - f );

  outColor += specular;
  outColor += envReflectionOuterOut * 0.75;

  outColor = rgb2hsb( outColor );
  outColor.z += 0.1;
  outColor = hsb2rgb( outColor );

  outColor.b += 0.07;
  outColor.r += 0.07;

  outColor *= 0.95;

  if(opacity < 0.1) discard;

  gl_FragColor = vec4( outColor, opacity);

}`;
